"use client";
import { useEffect, useState, useRef } from "react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import './styles.css'

interface Language {
    code: string;
    name: string;
    flag: string;
}

export function GtLanguages() {
    const [currentLanguage, setCurrentLanguage] = useState<string>("en");
    const googleTranslateInitialized = useRef(false);
    const selectRef = useRef<HTMLDivElement>(null);

    // Définition du tableau de langues disponibles
    const languages: Language[] = [
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'zh', name: '中文', flag: '🇨🇳' },
        { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    ];

    // Fonction pour détecter la langue actuelle depuis les cookies
    const detectLanguageFromCookies = () => {
        const match = document.cookie.match(/googtrans=\/[a-zA-Z-]+\/([a-zA-Z-]+)/);
        if (match && match[1]) {
            return match[1];
        }
        return "fr";
    };

    useEffect(() => {
        // Détecte la langue actuelle au chargement
        const detectedLang = detectLanguageFromCookies();
        if (detectedLang !== currentLanguage) {
            setCurrentLanguage(detectedLang);
        }

        // Fonction d'initialisation de Google Translate
        window.googleTranslateElementInit = () => {
            if (!googleTranslateInitialized.current) {
                new window.google.translate.TranslateElement(
                    {
                        pageLanguage: "fr",
                        includedLanguages: languages.map(lang => lang.code).join(","),
                        autoDisplay: false,
                        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
                    },
                    "google_translate_element"
                );
                googleTranslateInitialized.current = true;

                // Observer pour détecter les changements de langue par Google Translate
                const observer = new MutationObserver(() => {
                    const detectedLang = detectLanguageFromCookies();
                    if (detectedLang !== currentLanguage) {
                        setCurrentLanguage(detectedLang);
                    }
                });

                // Observe le body pour détecter les changements de classe qui indiquent une traduction
                observer.observe(document.body, {
                    attributes: true,
                    attributeFilter: ['class']
                });
            }
        };

        // Charge le script Google Translate
        if (!document.getElementById("google-translate-script")) {
            const script = document.createElement("script");
            script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
            script.id = "google-translate-script";
            document.head.appendChild(script);
        } else if (window.google && window.google.translate) {
            // Si le script est déjà chargé, initialise manuellement
            window.googleTranslateElementInit();
        }

        return () => {
            // Nettoyage si nécessaire
        };
    }, []);

    const handleLanguageChange = (value: string) => {
        if (value === currentLanguage) return;

        setCurrentLanguage(value);

        // Méthode 1: Utiliser le sélecteur de Google Translate s'il existe
        const googleCombo = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (googleCombo) {
            googleCombo.value = value;
            googleCombo.dispatchEvent(new Event('change'));
            return;
        }

        // Méthode 2: Manipuler directement les cookies et recharger
        const domain = window.location.hostname;

        // Supprime les anciens cookies
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;

        // Ajoute les nouveaux cookies
        document.cookie = `googtrans=/en/${value}; path=/;`;
        document.cookie = `googtrans=/en/${value}; path=/; domain=${domain};`;

        // Recharge la page pour appliquer les changements
        window.location.reload();
    };

    return (
        <div className="relative" ref={selectRef}>
            <Select value={currentLanguage} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-[64px] md:w-[180px]">
                    <SelectValue placeholder="Select language">
                        {languages.find(lang => lang.code === currentLanguage)?.flag || '🇫🇷'}&nbsp;&nbsp;
                        <span className="invisible md:visible">{languages.find(lang => lang.code === currentLanguage)?.name || "Français"}</span>
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {languages.map((language) => (
                        <SelectItem key={language.code} value={language.code}>
                            {language.flag}&nbsp;&nbsp;{language.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

// Déclarations TypeScript
declare global {
    interface Window {
        googleTranslateElementInit: () => void;
        google: {
            translate: {
                TranslateElement: any & {
                    InlineLayout: {
                        SIMPLE: number;
                        HORIZONTAL: number;
                        VERTICAL: number;
                    }
                };
            };
        };
    }
}
